import { Children, useEffect, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Check, ChevronDown, ChevronRight, Clock, HelpCircle, Hourglass, Inbox, ListTodo, Plus, RefreshCw, Star, Target, Trash2, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  KIND_LABELS,
  autoFilingOutcome,
  canRetryAutoFiling,
  containerOf,
  captureFilingState,
  confirmableSuggestion,
  defaultFilingRequest,
  describeOutcome,
  dueInFocusLine,
  gtdToday,
  itemRanks,
  latestClassification,
  localDateString,
  passedReminderNote,
  rankedOutcomes,
  todayLead,
  unfiledCaptureText,
  withoutArchived,
} from '@cerebro-diem/core';
import type { AutoFilingOutcome, ContainerRow, FilingRequest, FilingState, Task } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import { EdgeFunctionError, invokeEdgeFunction } from '../../services/edgeFunctions';
import { errorMessage, isRecord } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit, refreshFilingViews } from '../../services/queries';
import { deleteCapture, fileCaptureInto, inboxCaptures, recentCaptures, unfiledCaptureCount } from '../../services/captures';
import type { CaptureWithClassifications, Filed } from '../../services/captures';
import { containerPath, useContainers } from '../../services/containers';
import { useOutcomes, useTaskJudgments } from '../../services/outcomes';
import { useProfile } from '../../services/profile';
import TaskItem from '../../components/TaskItem';
import { chipButton } from '../../components/ContainerChip';
import { outlineButton, primaryButton, tapLink } from '../../components/styles';
import { useQuestions } from '../../services/brainDump';
import { useAuthStore } from '../../stores/authStore';
import FilingForm from '../filing/FilingForm';
import type { PersonName } from '../filing/FilingForm';
import { STATE_TONES, stateLabel, type InboxEntry } from './captureLabels';
import HomeWarnings from './HomeWarnings';
import PendingCaptures from './PendingCaptures';
import KeptDictationRecordings from './KeptDictationRecordings';
import { useTodaySections } from './todaySections';
import NotificationInvite from './NotificationInvite';
import TodayCalendar from '../calendar/TodayCalendar';

const INBOX_STATES: ReadonlySet<FilingState> = new Set<FilingState>(['filing', 'needs_review', 'failed', 'unfiled']);
const CLOCK_TICK_MS = 30_000;
const FILING_REFRESH_MS = 10_000;
const RECENT_CAPTURE_LIMIT = 5;


/** The message above the Inbox list about the last capture acted on, with a link to where it went. */
interface InboxNotice {
  message: string;
  link?: { to: string; label: string };
  /** A filed task: the notice names where it is now, so moving it does not leave the notice behind. */
  filedTask?: { id: string; describe: (containerName: string) => string };
}

/** The notice with a filed task's current container, when Today has the task. */
function liveNotice(notice: InboxNotice, tasks: readonly Task[], containers: readonly ContainerRow[]): InboxNotice {
  if (!notice.filedTask) return notice;
  const task = tasks.find((t) => t.id === notice.filedTask?.id);
  const ref = task ? containerOf(task) : null;
  const container = ref ? containers.find((c) => c.id === ref.id) : undefined;
  if (!ref || !container) return notice;
  return {
    message: notice.filedTask.describe(container.name),
    link: { to: containerPath(ref.kind, ref.id), label: `Open ${container.name}` },
  };
}

/** The current time, re-read on an interval so "today" and "Filing…" stay true on a page left open. */
function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function queryStatus(queries: { isPending: boolean; isError: boolean; error: unknown; data: unknown }[], what: string, keepCached = false): ReactNode {
  const failed = queries.find((q) => q.isError && (!keepCached || q.data === undefined));
  if (failed) {
    return (
      <p className="text-sm text-error">
        Could not load {what}: {errorMessage(failed.error)}
      </p>
    );
  }
  if (queries.some((q) => q.isPending)) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  return null;
}

export default function HomePage() {
  const dumpQuestions = useQuestions(useAuthStore((state) => state.user?.id ?? null));
  const [searchParams] = useSearchParams();
  const captureId = searchParams.get('capture');
  const now = useNow(CLOCK_TICK_MS);
  const today = localDateString(now);
  const [inboxNotice, setInboxNotice] = useState<InboxNotice | null>(null);

  const tasksQuery = useQuery({
    queryKey: queryKeys.todayTasks,
    queryFn: async () => {
      const { data, error } = await supabase.from('tasks').select('*').neq('status', 'done')
        // Past the cap, keep what Today shows first: focus, then due dates, then the newest.
        .order('focus_date', { ascending: false, nullsFirst: false })
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as Task[];
    },
  });
  const containersQuery = useContainers();
  const profileQuery = useProfile();
  const peopleQuery = useQuery({
    queryKey: [...queryKeys.people, 'names'],
    queryFn: async () => {
      const { data, error } = await supabase.from('people').select('id, name').order('name');
      if (error) throw error;
      return (data ?? []) as PersonName[];
    },
  });

  const inboxQuery = useQuery({
    queryKey: queryKeys.inbox,
    queryFn: inboxCaptures,
    // While anything is still being auto-filed, keep checking until it settles.
    refetchInterval: (query) =>
      query.state.data?.captures.some((capture) => captureFilingState(capture, latestClassification(capture)) === 'filing')
        ? FILING_REFRESH_MS
        : false,
  });

  // Under the captures key, so everything that refreshes the Inbox refreshes it too.
  const unfiledQuery = useQuery({ queryKey: queryKeys.unfiledCaptures, queryFn: unfiledCaptureCount });


  const recentQuery = useQuery({
    queryKey: [...queryKeys.captures, 'recent'],
    queryFn: () => recentCaptures(RECENT_CAPTURE_LIMIT),
  });

  const containers = containersQuery.data ?? [];
  const contexts = profileQuery.data?.gtd_contexts ?? [];
  // Tasks in an archived Project or Area are hidden from Today.
  const tasks = withoutArchived(tasksQuery.data ?? [], new Map(containers.map((c) => [c.id, c])));
  // With outcomes set, Today leads with the few highest-ranked next actions and
  // orders the rest by the same rank (CER-181); without them it is as it was.
  const outcomesQuery = useOutcomes();
  const judgmentsQuery = useTaskJudgments();
  const ranked = rankedOutcomes(outcomesQuery.data ?? []);
  const lead = judgmentsQuery.data ? todayLead(gtdToday(tasks, today, contexts), itemRanks(judgmentsQuery.data, ranked), ranked) : null;
  const view = lead?.view ?? gtdToday(tasks, today, contexts);
  const taskQueries = [tasksQuery, containersQuery];
  const tasksLoaded = tasksQuery.data !== undefined && containersQuery.data !== undefined;
  const nextCount = view.next.reduce((sum, group) => sum + group.tasks.length, 0);
  const dueCount = view.due.length + view.dueInFocus.length;
  const dueInFocus = dueInFocusLine(view.dueInFocus, today);
  const captures = inboxQuery.data?.captures ?? [];
  const inbox: InboxEntry[] = captures
    .map((capture) => {
      const classification = latestClassification(capture);
      return { capture, classification, state: captureFilingState(capture, classification, now) };
    })
    .filter((entry) => INBOX_STATES.has(entry.state) && entry.capture.id !== captureId);
  // Captures the server has not filed or sent to review yet: its sweep keeps filing them (CER-78).
  const unfiledLine = unfiledQuery.isError
    ? `Could not count captures not filed yet: ${errorMessage(unfiledQuery.error)}`
    : unfiledQuery.data === undefined
      ? null
      : unfiledCaptureText(unfiledQuery.data);
  const notice = inboxNotice ? liveNotice(inboxNotice, tasksQuery.data ?? [], containers) : null;
  const sections = useTodaySections();
  const inboxShown = !inboxQuery.isSuccess || inbox.length > 0 || notice !== null || unfiledLine !== null || unfiledQuery.isError;
  const taskList = (list: Task[]) => (
    <ul className="space-y-2">
      {list.map((task) => (
        <li key={task.id}>
          <TaskItem task={task} today={today} containers={containers} />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Today</h1>
          <p className="text-on-surface-variant">{format(now, 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <button type="button" onClick={() => sections.setAll(!sections.allExpanded)} className={`${tapLink} text-sm text-primary`}>
          {sections.allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </header>

      {captureId && (
        <SelectedCapture
          key={captureId}
          captureId={captureId}
          filingStatus={queryStatus([containersQuery], 'projects, areas and resources')}
          containers={containers}
          people={peopleQuery.data ?? []}
          contexts={profileQuery.data?.gtd_contexts ?? null}
          timeZone={profileQuery.data?.timezone ?? null}
          onNotice={setInboxNotice}
        />
      )}

      <HomeWarnings />
      {/* A brain dump's questions still to answer (CER-236): their thoughts are saved once answered or skipped. */}
      {dumpQuestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-secondary-container p-4">
          <p className="flex-1 font-medium">
            {dumpQuestions.length} {dumpQuestions.length === 1 ? 'thought' : 'thoughts'} from your brain dump {dumpQuestions.length === 1 ? 'needs' : 'need'} a word
          </p>
          <Link to="/brain-dump" className={primaryButton}>
            Answer
          </Link>
        </div>
      )}

      <PendingCaptures />
      <KeptDictationRecordings />

      {/* Asked once this browser has been used for something, not on first paint,
          and whether or not a reminder is queued (CER-150). */}
      <NotificationInvite used={(recentQuery.data?.captures.length ?? 0) > 0} />

      {reachedLimit(tasksQuery.data) && (
        <p role="status" className="text-sm text-tertiary">
          Today reads {LIST_LIMIT.toLocaleString()} of your open tasks (focus and due first, then the newest); older undated ones are not shown here.{' '}
          <Link to="/tasks" className="font-medium underline">
            All tasks
          </Link>
        </p>
      )}

      <TodayCalendar today={today} />
      {([
        [tasksQuery, 'tasks'], [containersQuery, 'projects and areas'], [profileQuery, 'task preferences'],
      ] as const).map(([query, what]) => query.isError && query.data !== undefined && (
        <p key={what} className="text-sm text-error">Could not refresh {what}: {errorMessage(query.error)}</p>
      ))}

      {[[outcomesQuery, 'your outcomes'] as const, [judgmentsQuery, 'the scores for your tasks'] as const].map(([query, what]) => query.isError && (
        <p key={what} className="text-sm text-error">Could not load {what}, so Today is not ordered by them: {errorMessage(query.error)}</p>
      ))}
      {/* The few things that matter most, always open: this is what Today is for. */}
      {tasksLoaded && lead && lead.lead.length > 0 && (
        <Section id="lead" icon={Star} title="What matters now" attention>
          <ul className="space-y-2">
            {lead.lead.map(({ task, outcome }) => (
              <li key={task.id}>
                <TaskItem task={task} today={today} containers={containers} serves={outcome.title} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* An empty section is its heading alone: no count, no line saying it is empty. */}
      {/* Nothing picked for today yet: the phone's morning check-in is where focus is chosen. */}
      <Section
        id="focus"
        icon={Target}
        title="Focus"
        count={tasksLoaded ? view.focus.length : undefined}
        aside={tasksLoaded && view.focus.length === 0 && <span className="text-sm text-on-surface-variant">Nothing picked yet; the morning check-in picks it</span>}
      >
        {queryStatus(taskQueries, 'tasks', true) ?? (view.focus.length > 0 && taskList(view.focus))}
      </Section>

      {/* Shown only when there is something to deal with: a capture waiting, one being filed, a filing just
          made, or a count that could not be read. An empty Inbox is the normal case and takes no room. */}
      {inboxShown && (
        <Section id="inbox" icon={Inbox} title="Inbox" count={inboxQuery.isSuccess ? inbox.length : undefined} attention={notice !== null || unfiledQuery.isError}>
          {unfiledLine && (
            <p role="status" className={`text-sm mb-3 ${unfiledQuery.isError ? 'text-error' : 'text-on-surface-variant'}`}>
              {unfiledLine}
            </p>
          )}
          {notice && (
            <p role="status" className="text-sm text-success mb-3">
              {notice.message}
              {notice.link && (
                <>
                  {' '}
                  <Link to={notice.link.to} className="font-medium underline">
                    {notice.link.label}
                  </Link>
                </>
              )}
            </p>
          )}
          {queryStatus([inboxQuery, containersQuery], 'the inbox') ??
            (inbox.length > 0 && (
              <ul className="space-y-3">
                {inbox.map((entry) => (
                  <InboxItem
                    key={entry.capture.id}
                    entry={entry}
                    containers={containers}
                    people={peopleQuery.data ?? []}
                    contexts={profileQuery.data?.gtd_contexts ?? null}
                    timeZone={profileQuery.data?.timezone ?? null}
                    onNotice={setInboxNotice}
                  />
                ))}
              </ul>
            ))}
          {inboxQuery.data?.hasMore && (
            <p className="text-xs text-on-surface-variant mt-3">Showing the newest captures only. File or delete these to see older ones.</p>
          )}
        </Section>
      )}

      {/* A focused task that is due stays in Focus; Due counts it and names it, so Due never reads as nothing due. */}
      <Section id="due" icon={Clock} title="Due" count={tasksLoaded ? dueCount : undefined}>
        {queryStatus(taskQueries, 'tasks', true) ??
          (dueCount > 0 && (
            <div className="space-y-2">
              {view.due.length > 0 && taskList(view.due)}
              {dueInFocus && <p className="text-sm text-on-surface-variant">{dueInFocus}</p>}
            </div>
          ))}
      </Section>

      <Section id="next" icon={ListTodo} title="Next actions" count={tasksLoaded ? nextCount : undefined}>
        {queryStatus([...taskQueries, profileQuery], 'next actions', true) ??
          (nextCount > 0 && (
            <div className="space-y-4">
              {view.next.map((group) => {
                // Each context is its own collapsed group: its name and how many, opened by a tap.
                const key = `next:${group.context ?? ''}`;
                const open = sections.isOpen(key);
                return (
                  <div key={group.context ?? ''} className="space-y-2">
                    <h3>
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => sections.toggle(key)}
                        className={`${tapLink} gap-1 text-sm font-semibold text-on-surface-variant`}
                      >
                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        {group.context ? `@${group.context}` : 'No context'}
                        <span className="font-normal">{group.tasks.length}</span>
                      </button>
                    </h3>
                    {open && taskList(group.tasks)}
                  </div>
                );
              })}
            </div>
          ))}
        {tasksLoaded && view.somedayCount > 0 && (
          <p className="text-sm text-on-surface-variant mt-3">
            {view.somedayCount} someday {view.somedayCount === 1 ? 'task is' : 'tasks are'} left for your weekly review.{' '}
            <Link to="/tasks" className="text-primary font-medium hover:underline">
              All tasks
            </Link>
          </p>
        )}
      </Section>

      {/* Next actions with no confident rank, gathered so they can be looked at and fixed. */}
      {tasksLoaded && lead && lead.unranked.length > 0 && (
        <Section id="unranked" icon={HelpCircle} title="Not ranked yet" count={lead.unranked.length}>
          <p className="text-sm text-on-surface-variant mb-3">
            These could not be placed against your outcomes with confidence, or have not been scored yet. Rewording one so it says what it
            is for usually places it.{' '}
            <Link to="/outcomes" className="font-medium underline">
              What matters most
            </Link>
          </p>
          {taskList(lead.unranked)}
        </Section>
      )}

      <Section id="waiting" icon={Hourglass} title="Waiting for" count={tasksLoaded ? view.waiting.length : undefined}>
        {queryStatus(taskQueries, 'tasks', true) ?? (view.waiting.length > 0 && taskList(view.waiting))}
      </Section>

    </div>
  );
}

/** A search result is read directly, even when it is older than the Inbox list. */
function SelectedCapture({ captureId, filingStatus, ...props }: { captureId: string; filingStatus: ReactNode } & Omit<ComponentProps<typeof InboxItem>, 'entry' | 'initiallyOpen'>) {
  const region = useRef<HTMLDivElement>(null);
  const captureQuery = useQuery({
    queryKey: [...queryKeys.captures, 'one', captureId],
    queryFn: async () => {
      const { data, error } = await supabase.from('captures').select('*, classifications(*)').eq('id', captureId).maybeSingle();
      if (error) throw error;
      return data as CaptureWithClassifications | null;
    },
  });
  useEffect(() => {
    region.current?.focus();
  }, [captureQuery.isPending]);
  const capture = captureQuery.data;
  const classification = capture ? latestClassification(capture) : null;
  const state = capture ? captureFilingState(capture, classification) : null;
  const message = captureQuery.isError
    ? `Could not load this capture: ${errorMessage(captureQuery.error)}`
    : captureQuery.isPending
    ? 'Loading this capture…'
    : !capture
    ? 'This capture no longer exists.'
    : state === 'filed'
    ? 'This capture is already filed.'
    : null;
  return (
    <div ref={region} tabIndex={-1} aria-label="Selected capture">
      {/* The capture someone came here for is always shown. */}
      <Section id="capture" icon={Inbox} title="Capture" attention>
        {message ? (
          <p role="status" className={`text-sm ${captureQuery.isError ? 'text-error' : 'text-on-surface-variant'}`}>{message}</p>
        ) : filingStatus ?? (capture && state && (
          <ul>
            <InboxItem {...props} entry={{ capture, classification, state }} initiallyOpen />
          </ul>
        ))}
      </Section>
    </div>
  );
}

/**
 * One of Today's sections. It starts collapsed: its heading and count, opened
 * by a tap and remembered on this device. A section with nothing in it is its
 * heading alone, not a button. `attention` opens it regardless, for something
 * the user must not miss.
 */
function Section({
  id,
  icon: Icon,
  title,
  count,
  aside,
  attention = false,
  children,
}: {
  id: string;
  icon: LucideIcon;
  title: string;
  count?: number;
  aside?: ReactNode;
  attention?: boolean;
  children: ReactNode;
}) {
  const sections = useTodaySections();
  const hasContent = Children.toArray(children).length > 0;
  const open = hasContent && (attention || sections.isOpen(id));
  const heading = (
    <>
      <Icon className="w-5 h-5 text-primary" />
      <h2 className="text-lg font-semibold">{title}</h2>
      {/* A count of zero says no more than the empty section does. */}
      {count !== undefined && count > 0 && <span className="text-sm text-on-surface-variant">{count}</span>}
    </>
  );
  return (
    <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${open ? 'mb-3' : ''}`}>
        {hasContent ? (
          <button type="button" aria-expanded={open} onClick={() => sections.toggle(id)} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left">
            {open ? <ChevronDown className="w-4 h-4 text-on-surface-variant" /> : <ChevronRight className="w-4 h-4 text-on-surface-variant" />}
            {heading}
          </button>
        ) : heading}
        {aside}
      </div>
      {open && children}
    </section>
  );
}

/** What a filing did, with a link to the container it went into. */
function filedNotice(filed: Filed, request: FilingRequest, containers: readonly ContainerRow[]): InboxNotice {
  const name = 'newName' in request.container
    ? request.container.newName
    : (containers.find((c) => c.id === filed.containerId)?.name ?? 'its container');
  const describe = (where: string) => `Filed as ${KIND_LABELS[filed.kind]} in ${where}: ${request.title}.`;
  return {
    message: describe(name),
    link: { to: containerPath(filed.containerKind, filed.containerId), label: `Open ${name}` },
    ...(filed.kind === 'task' ? { filedTask: { id: filed.destinationId, describe } } : {}),
  };
}

/** What a finished retry did. `settled`: the capture was filed or classified meanwhile. */
function retryNotice(outcome: AutoFilingOutcome | 'settled'): InboxNotice {
  if (outcome === 'settled') {
    return { message: 'That capture was already filed or classified; the Inbox is refreshed.' };
  }
  if (outcome.status === 'needs_review') {
    return { message: `AI filing ran again and suggests ${describeOutcome(outcome)}; confirm it below.` };
  }
  const { container } = outcome;
  return {
    message: `AI filed it as ${describeOutcome(outcome)}.`,
    link: { to: containerPath(container.kind, container.id), label: `Open ${container.name ?? 'it'}` },
    ...(outcome.category === 'task'
      ? { filedTask: { id: outcome.destinationId, describe: (where: string) => `AI filed it as ${KIND_LABELS.task} in ${where}.` } }
      : {}),
  };
}

/** Run AI filing again. Throws with the server's reason when it still cannot file the capture. */
async function retryAutoFiling(captureId: string): Promise<AutoFilingOutcome | 'settled'> {
  let result: unknown;
  try {
    result = await invokeEdgeFunction('classify-capture', { capture_id: captureId });
  } catch (err) {
    // Filed or classified meanwhile (by hand, or on another device): nothing failed.
    if (err instanceof EdgeFunctionError && err.status === 409) return 'settled';
    throw err;
  }
  const outcome = autoFilingOutcome(isRecord(result) ? result.classification : null);
  if (!outcome) throw new Error('AI filing answered without saying where the capture went');
  return outcome;
}

function InboxItem({
  initiallyOpen = false,
  entry,
  containers,
  people,
  contexts,
  timeZone,
  onNotice,
}: {
  initiallyOpen?: boolean;
  entry: InboxEntry;
  containers: readonly ContainerRow[];
  people: readonly PersonName[];
  /** The user's contexts; null until they are known (Confirm waits for them). */
  contexts: readonly string[] | null;
  /** The user's zone, in which a suggested reminder is judged; null until set. */
  timeZone: string | null;
  onNotice: (notice: InboxNotice) => void;
}) {
  const { capture, classification, state } = entry;
  const [filing, setFiling] = useState(initiallyOpen);
  const [addedPeople, setAddedPeople] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const suggestion = state === 'needs_review' ? classification : null;
  const clock = { timeZone, now: new Date() };
  const confirmRequest =
    suggestion && contexts ? confirmableSuggestion(capture.raw_text, suggestion, containers, contexts, clock) : null;
  const proposedPeople = suggestion ? defaultFilingRequest(capture.raw_text, suggestion, containers, clock).proposedPeople : [];
  // Why it was not filed when the reminder it asked for had passed by then (CER-81).
  const reminderNote = passedReminderNote(suggestion, timeZone, clock.now);

  const confirmMutation = useMutation({
    mutationFn: (request: FilingRequest) => fileCaptureInto(capture.id, request),
    onSuccess: (filed, request) => {
      onNotice(filedNotice(filed, request, containers));
      return refreshFilingViews(queryClient);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteCapture(capture.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.captures }),
  });
  const retryMutation = useMutation({
    mutationFn: () => retryAutoFiling(capture.id),
    onSuccess: (outcome) => onNotice(retryNotice(outcome)),
    // Filed, sent to review, or failed with a new reason: the Inbox changed either way.
    onSettled: () => refreshFilingViews(queryClient),
  });
  const busy = deleteMutation.isPending || retryMutation.isPending || confirmMutation.isPending;

  const confirmDelete = () => {
    if (window.confirm('Delete this capture? This cannot be undone.')) deleteMutation.mutate();
  };
  const toggleAdded = (name: string) =>
    setAddedPeople((current) => (current.includes(name) ? current.filter((n) => n !== name) : [...current, name]));

  return (
    <li className="p-3 rounded-xl border border-outline/20 space-y-2">
      {/* The capture itself opens it, as clicking a task opens the task; the
          actions below keep their own clicks. */}
      <button
        type="button"
        aria-label="Open this capture"
        onClick={() => setFiling(true)}
        disabled={busy || filing}
        className="w-full text-left space-y-2"
      >
        <p className="text-sm whitespace-pre-wrap break-words">{capture.raw_text}</p>
        <p className="text-xs flex flex-wrap gap-x-2">
          <span className="text-on-surface-variant">{format(new Date(capture.created_at), 'MMM d, h:mm a')}</span>
          <span className={`min-w-0 ${STATE_TONES[state]}`}>{stateLabel(entry, containers)}</span>
        </p>
      </button>
      {reminderNote && <p className="text-xs text-tertiary">{reminderNote}</p>}

      {filing ? (
        <FilingForm
          capture={capture}
          suggestion={classification}
          containers={containers}
          people={people}
          contexts={contexts ?? []}
          onFiled={(filed, request) => onNotice(filedNotice(filed, request, containers))}
          onCancel={() => setFiling(false)}
        />
      ) : (
        <>
          {confirmRequest && proposedPeople.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-on-surface-variant">People:</span>
              {proposedPeople.map((name) => {
                const added = addedPeople.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={added}
                    onClick={() => toggleAdded(name)}
                    className={`${chipButton} ${added ? 'bg-primary-container text-primary' : 'border border-outline/30 text-on-surface-variant'}`}
                  >
                    {added ? (
                      <>
                        {name} (new) <X className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        <Plus className="w-3 h-3" /> Add {name}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {confirmRequest && (
              <button
                type="button"
                onClick={() => confirmMutation.mutate({ ...confirmRequest, newPersonNames: addedPeople })}
                disabled={busy}
                className={`${primaryButton} inline-flex items-center gap-1`}
              >
                <Check className="w-4 h-4" />
                {confirmMutation.isPending ? 'Filing…' : 'Confirm'}
              </button>
            )}
            {canRetryAutoFiling(state) && (
              <button
                type="button"
                onClick={() => retryMutation.mutate()}
                disabled={busy}
                className={`${primaryButton} inline-flex items-center gap-1`}
              >
                <RefreshCw className={`w-4 h-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                {retryMutation.isPending ? 'Retrying AI filing…' : 'Retry AI filing'}
              </button>
            )}
            <button type="button" onClick={() => setFiling(true)} disabled={busy} className={outlineButton}>
              File…
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={busy}
              className={`${outlineButton} inline-flex items-center gap-1 text-error`}
            >
              <Trash2 className="w-4 h-4" />
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </>
      )}

      {confirmMutation.isError && <p className="text-xs text-error">Not filed: {errorMessage(confirmMutation.error)}</p>}
      {retryMutation.isError && (
        <p className="text-xs text-error">Retried; still not filed: {errorMessage(retryMutation.error)}</p>
      )}
      {deleteMutation.isError && <p className="text-xs text-error">Not deleted: {errorMessage(deleteMutation.error)}</p>}
    </li>
  );
}
